var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { injectable } from 'inversify';
import { validateOrReject, validate } from 'class-validator';
import { plainToClass } from 'class-transformer';
import at from 'lodash/at';
import intersection from 'lodash/intersection';
import get from 'lodash/get';
import { FileType } from '../file-type';
import { AppError } from '../error';
const NUMBER_B_IN_MB = 1048576;
const DEFAULT_SIZE_LIMIT = 20;
const MAX_HOURS = 23;
const MAX_MINUTES = 59;
const EXCLUDE_EXTENSION = ['jfif'];
const getImageResolution = (file) => {
    const image = new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                resolve({ width: img.width, height: img.height });
            };
            img.onerror = reject;
            img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
    return image;
};
const getFileExtension = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.slice(lastDotIndex + 1).toLowerCase() : '';
};
const validateFile = (file, message, fileTypes, fileTypeChecker, skipFileTypeValidation, maxSize) => __awaiter(void 0, void 0, void 0, function* () {
    const extension = getFileExtension(file.name);
    if (EXCLUDE_EXTENSION.includes(extension)) {
        return message;
    }
    let fileType = skipFileTypeValidation
        ? file.type
        : yield fileTypeChecker.fileTypeFromBlob(file);
    if (file === null || file === void 0 ? void 0 : file.name.includes('.sig')) {
        fileType = 'application/pgp-signature';
    }
    else if (file === null || file === void 0 ? void 0 : file.name.includes('.p7m')) {
        fileType = 'application/pkcs7-mime';
    }
    return !fileTypes.includes(fileType) ||
        file.size > (maxSize !== null && maxSize !== void 0 ? maxSize : DEFAULT_SIZE_LIMIT) * NUMBER_B_IN_MB
        ? message
        : undefined;
});
const validateFileNext = (fileMeta, message, fileTypes, maxSize) => __awaiter(void 0, void 0, void 0, function* () {
    const extension = getFileExtension(fileMeta.name);
    if (EXCLUDE_EXTENSION.includes(extension)) {
        return message;
    }
    let fileType = fileMeta.mimeType;
    if (fileMeta.name.includes('.sig')) {
        fileType = 'application/pgp-signature';
    }
    else if (fileMeta.name.includes('.p7m')) {
        fileType = 'application/pkcs7-mime';
    }
    return !fileTypes.includes(fileType) ||
        fileMeta.size > (maxSize !== null && maxSize !== void 0 ? maxSize : DEFAULT_SIZE_LIMIT) * NUMBER_B_IN_MB
        ? message
        : undefined;
});
const errorsToAllStrings = (value, errors) => {
    const result = Array.isArray(value)
        ? []
        : {};
    if (!errors || !errors.length)
        return result;
    errors.forEach(({ property, constraints, children }) => {
        if (!constraints && children && (children === null || children === void 0 ? void 0 : children.length) !== 0) {
            const propertyName = property;
            const childrenResult = errorsToAllStrings(value[propertyName], children);
            result[property] = childrenResult;
        }
        else {
            result[property] = constraints
                ? constraints[Object.keys(constraints)[0]]
                : 'Value is invalid';
        }
    });
    return result;
};
let ValidationService = class ValidationService {
    constructor(fileType) {
        this.fileType = fileType;
        /**
         * Создать функцию для Wysiwyg, которая в случае "пустого" значения выведет заданное сообщение об ошибке
         *
         * @param message - Сообщение об ошибке в случае, если значение "пустое"
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.requiredWysiwyg = (message = 'Обязательное поле') => (value) => typeof value === 'undefined' || value === null || !(value === null || value === void 0 ? void 0 : value.hasContent)
            ? message
            : undefined;
        /**
         * Создать функцию, которая в случае "пустого" значения выведет заданное сообщение об ошибке
         *
         * @typeParam FieldType - Тип значения поля
         * @param message - Сообщение об ошибке в случае, если значение "пустое"
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.required = (message = 'Обязательное поле') => (value) => typeof value === 'undefined' ||
            value === null ||
            (typeof value === 'boolean' && value === false) ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0)
            ? message
            : undefined;
        /**
         * Определить является ли текущее валидируемое поле обязательным
         * Текущее валидируемое поле зависит от другого поля (определено в options.field).
         * Если значение поля field (из options.field) не присутствует в in (из options.in),
         * то текущее валидируемое поле является обязательным, иначе необязательным.
         *
         * @typeParam T - Тип значения поля field
         * @typeParam FieldType - Тип валидируемого поля
         * @param message - Сообщение об ошибке
         * @param options - Параметры зависимости: поле, массив значений и тип операции
         * @returns Функция валидатора final-form
         */
        this.requiredIfNotIn = (message = 'Обязательное поле', options) => (value, allValues) => {
            const relatedAttributeValue = at(allValues, options.field)[0];
            const valueToCompare = Array.isArray(relatedAttributeValue)
                ? relatedAttributeValue
                : [relatedAttributeValue];
            const isIncludes = !!intersection(options.in, valueToCompare).length;
            return allValues &&
                relatedAttributeValue &&
                (options.operation === 'ex' ? isIncludes : !isIncludes)
                ? this.required(message)(value)
                : undefined;
        };
        /**
         * Определить является ли текущее валидируемое поле обязательным
         * Текущее валидируемое поле зависит от значений других полей (определены в options.conditions).
         * Поле является обязательным, когда все значения других полей (из options.conditions)
         * соответствуют значениям из equal или не соответствуют значениям unequal,
         * определенным для каждого поля
         *
         * @typeParam FieldType - Тип валидируемого поля
         * @param message - Сообщение об ошибке
         * @param options - Параметры зависимости: префикс и условия валидации
         * @returns Функция валидатора final-form
         */
        this.requiredIfMeetsConditions = (message = 'Обязательное поле', options) => (value, allValues) => {
            const meetsConditions = options === null || options === void 0 ? void 0 : options.conditions.every((condition) => {
                const fieldValue = at(allValues, `${options.prefix}_attribute_${condition.attribute_id}`)[0];
                const fieldValueArr = Array.isArray(fieldValue)
                    ? fieldValue
                    : [fieldValue];
                if (condition.equal) {
                    return condition.equal.every((item) => fieldValueArr.includes(item));
                }
                else if (condition.unequal) {
                    return condition.unequal.every((item) => !fieldValueArr.includes(item));
                }
            });
            return allValues && meetsConditions
                ? this.required(message)(value)
                : undefined;
        };
        /**
         * Создать функцию, которая будет возвращать предупреждающее сообщение, если заданное строковое значение дублируется
         *
         * @param message - Сообщение, указывающее, что значение дублируется
         * @param options - Опции, содержат список для проверки на дубликаты
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.notInList = (message = 'Такое название уже существует', options) => (value) => {
            if (typeof value !== 'string') {
                return undefined;
            }
            const sameRole = options.notIn.find((name) => name.toLowerCase() === value.toLowerCase());
            return sameRole ? message : undefined;
        };
        /**
         * Создать функцию, которая будет возвращать предупреждающее сообщение,
         * если заданное значение не относится к допустимым типам файлов или файл превышает допустимый размер
         *
         * @param message - Сообщение, указывающее, что значение имеет недопустимый тип или размер
         * @param options - Опции, содержат список допустимых типов
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.requiredIfFile = (message = 'Обязательное поле', options) => {
            const fileTypeChecker = this.fileType;
            return function validateValue(value) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!value ||
                        typeof value === 'string' ||
                        typeof (value === null || value === void 0 ? void 0 : value.source) === 'string')
                        return undefined;
                    if (Array.isArray(value)) {
                        const res = yield value.reduce((error, arrItem) => __awaiter(this, void 0, void 0, function* () {
                            return (yield error) || (yield validateValue(arrItem));
                        }), Promise.resolve(undefined));
                        return res;
                    }
                    const result = yield validateFile((value === null || value === void 0 ? void 0 : value.source) || value, message, options.fileTypes, fileTypeChecker, options.skipFileTypeValidation, options.maxSize);
                    return result;
                });
            };
        };
        this.requiredIfFileNext = (message = 'Обязательное поле', options) => {
            return function validateValue(value) {
                return __awaiter(this, void 0, void 0, function* () {
                    var _a, _b;
                    if (Array.isArray(value)) {
                        const res = yield value.reduce((error, arrItem, index) => __awaiter(this, void 0, void 0, function* () {
                            var _a;
                            const name = ((_a = arrItem.meta) === null || _a === void 0 ? void 0 : _a.name) || index;
                            const err = yield error;
                            const validationError = yield validateValue(arrItem);
                            if (validationError) {
                                if (err) {
                                    return `${err}, ${name}`;
                                }
                                return `${validationError}: ${name}`;
                            }
                            return err;
                        }), Promise.resolve(undefined));
                        return res;
                    }
                    if (!value ||
                        typeof value === 'string' ||
                        ((_a = value === null || value === void 0 ? void 0 : value.value) === null || _a === void 0 ? void 0 : _a.type) === 'server' ||
                        ((_b = value === null || value === void 0 ? void 0 : value.source) === null || _b === void 0 ? void 0 : _b.type) !== 'local' ||
                        !(value === null || value === void 0 ? void 0 : value.meta))
                        return undefined;
                    const result = yield validateFileNext(value.meta, message, options.fileTypes, options.maxSize);
                    return result;
                });
            };
        };
        this.maxValue = (max, message) => (value) => Number(value) > Number(max) ? message : undefined;
        this.minValue = (min, message) => (value) => Number(value) < min ? message : undefined;
        /**
         * Определить попадает ли значение в заданный диапазон
         *
         * @param min - Минимальное значение диапазона
         * @param max - Максимальное значение диапазона
         * @param message - Сообщение об ошибке в случае, если значение не попадает в заданный диапазон
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.rangeValue = (min, max, message) => (value) => Number(value) < min || Number(value) > max ? message : undefined;
        /**
         * Создать функцию, которая будет возвращать предупреждающее сообщение, если переданное значение превышает указанный в опциях лимит
         *
         * @typeParam T - Тип объекта значений
         * @typeParam K - Ключ объекта
         * @param message - Сообщение, указывающее, что значение превышает лимит
         * @param options - Опции, содержат лимит
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.sizeLimit = (message = 'Объем файла слишком большой', options) => function validateValue(value, allValues) {
            var _a, _b;
            const sizeLimit = options.sizeLimit * NUMBER_B_IN_MB;
            if (!value ||
                typeof value === 'string' ||
                typeof (value === null || value === void 0 ? void 0 : value.source) === 'string')
                return undefined;
            if (Array.isArray(value)) {
                const res = value.reduce((error, arrItem) => {
                    return error || validateValue(arrItem, allValues);
                }, undefined);
                return res;
            }
            else {
                const currentValueInForm = at(allValues, options.field)[0];
                if (currentValueInForm) {
                    return currentValueInForm &&
                        ((_a = value === null || value === void 0 ? void 0 : value.source) === null || _a === void 0 ? void 0 : _a.size) > sizeLimit
                        ? message
                        : undefined;
                }
                else {
                    return ((_b = value.source) === null || _b === void 0 ? void 0 : _b.size) > sizeLimit
                        ? message
                        : undefined;
                }
            }
        };
        this.sizeLimitNext = (message = 'Объем файла слишком большой', options) => function validateValue(value, allValues) {
            var _a, _b, _c, _d;
            const sizeLimit = options.sizeLimit * NUMBER_B_IN_MB;
            if (Array.isArray(value)) {
                const res = value.reduce((error, arrItem, index) => {
                    var _a;
                    const name = ((_a = arrItem.meta) === null || _a === void 0 ? void 0 : _a.name) || index;
                    const validationError = validateValue(arrItem, allValues);
                    if (validationError) {
                        if (error) {
                            return `${error}, ${name}`;
                        }
                        return `${validationError}: ${name}`;
                    }
                    return error;
                }, undefined);
                return res;
            }
            if (!value ||
                typeof value === 'string' ||
                ((_a = value === null || value === void 0 ? void 0 : value.value) === null || _a === void 0 ? void 0 : _a.type) === 'server' ||
                ((_b = value === null || value === void 0 ? void 0 : value.source) === null || _b === void 0 ? void 0 : _b.type) !== 'local' ||
                !(value === null || value === void 0 ? void 0 : value.meta))
                return undefined;
            const currentValueInForm = at(allValues, options.field)[0];
            if (currentValueInForm) {
                const size = ((_c = value === null || value === void 0 ? void 0 : value.meta) === null || _c === void 0 ? void 0 : _c.size) || 0;
                const res = currentValueInForm && size > sizeLimit ? message : undefined;
                return res;
            }
            else {
                const size = ((_d = value === null || value === void 0 ? void 0 : value.meta) === null || _d === void 0 ? void 0 : _d.size) || 0;
                return size > sizeLimit ? message : undefined;
            }
        };
        /**
         * Проверяет, что ширина и высота каждого файла не меньше заданных значений.
         * При несоответствии возвращает сообщение об ошибке для первого невалидного файла.
         *
         * @param message - Сообщение, добавляемое к ошибке, если разрешение недостаточно
         * @param options - Минимально допустимые размеры
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.imageResolutionCheck = (message, options) => {
            return function validateValue(value) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!value ||
                        typeof value === 'string' ||
                        typeof (value === null || value === void 0 ? void 0 : value.source) === 'string') {
                        return undefined;
                    }
                    if (Array.isArray(value)) {
                        const res = yield value.reduce((error, arrItem) => {
                            return error || validateValue(arrItem);
                        }, Promise.resolve(undefined));
                        return res;
                    }
                    else {
                        const { width, height } = yield getImageResolution(value.source);
                        if (width < options.minWidth)
                            return message;
                        if (height < options.minHeight)
                            return message;
                    }
                });
            };
        };
        /**
         * Создать функцию, которая будет возвращать предупреждающее сообщение, если переданное значение превышает указанный в опциях лимит
         *
         * @param options - Опции, содержат attributeId (id поля) и опционально числовой валидатор
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.moreOrLess = (options) => (value) => {
            if (!value) {
                return undefined;
            }
            const isNumber = /^\d+(\.\d+)?$/.test(value);
            const numberWithPoint = !isNaN(+value) && String(value).includes('.');
            if (!isNumber && !numberWithPoint) {
                return 'Данные должны быть числовым значением';
            }
            if (options.validator.greater_than) {
                const numberValue = Number(value);
                return numberValue > options.validator.greater_than
                    ? undefined
                    : `Число должно быть больше чем ${options.validator.greater_than}`;
            }
            if (options.validator.greater_than_or_equal) {
                const numberValue = Number(value);
                return numberValue >= options.validator.greater_than_or_equal
                    ? undefined
                    : `Число должно быть больше или равно ${options.validator.greater_than_or_equal}`;
            }
            if (options.validator.less_than) {
                const numberValue = Number(value);
                return numberValue < options.validator.less_than
                    ? undefined
                    : `Число должно быть меньше чем ${options.validator.less_than}`;
            }
            if (options.validator.less_than_or_equal) {
                const numberValue = Number(value);
                return numberValue <= options.validator.less_than_or_equal
                    ? undefined
                    : `Число должно быть меньше или равно ${options.validator.less_than_or_equal}`;
            }
        };
        /**
         * Проверяет, что значение является корректным URL с разрешённым протоколом
         *
         * @param message - Сообщение об ошибке в случае, если значение не является корректным URL или протокол не входит в разрешённый список
         * @param allowedProtocols - Массив разрешённых протоколов. По умолчанию: http:, https:, mailto:, tel:, sms:
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.validUrl = (message = 'Значение поля должно быть ссылкой', allowedProtocols = [
            'http:',
            'https:',
            'mailto:',
            'tel:',
            'sms:',
        ]) => (value) => {
            if (!value)
                return undefined;
            try {
                const parsedUrl = new URL(value);
                if (!allowedProtocols.includes(parsedUrl.protocol)) {
                    return message;
                }
                return undefined;
            }
            catch (_) {
                return message;
            }
        };
        this.maxLength = (max, message) => (value) => {
            if (!value)
                return undefined;
            return String(value).length > max ? message : undefined;
        };
        this.minLength = (min, message) => (value) => {
            if (!value)
                return undefined;
            return String(value).length < Number(min) ? message : undefined;
        };
        this.checkLength = (min, max, message) => (value) => {
            if (!value)
                return undefined;
            return String(value).length < Number(min) || String(value).length > max
                ? message
                : undefined;
        };
        this.dateNotInPast = (message, minutes) => (value) => {
            if (!value)
                return undefined;
            let combinedDateTime = new Date(`${value}T23:59`);
            if (minutes && new Date(`${value}T${minutes}`) > combinedDateTime) {
                const combinedDateTimeString = `${value}T${minutes}`;
                combinedDateTime = new Date(combinedDateTimeString);
            }
            return combinedDateTime < new Date() ? message : undefined;
        };
        this.timeNotInPast = (message, fieldName) => (value, allValues) => {
            const date = get(allValues, fieldName);
            if (!value || new Date(`${date}T23:59`) < new Date())
                return undefined;
            const today = new Date();
            let combinedDateTime = new Date(`${today.toDateString()} ${value}:59`);
            if (date && new Date(date) >= combinedDateTime) {
                const combinedDateTimeString = `${date}T${value}`;
                combinedDateTime = new Date(combinedDateTimeString);
            }
            return combinedDateTime < today ? message : undefined;
        };
        this.isValidTime = (message) => (value = '') => {
            const [hoursStr = '', minutesStr = ''] = value.split(':');
            const hours = Number(hoursStr);
            const minutes = Number(minutesStr);
            const invalid = isNaN(hours) ||
                isNaN(minutes) ||
                hours > MAX_HOURS ||
                minutes > MAX_MINUTES;
            return invalid ? message : undefined;
        };
        /**
         * Проверяет, является ли текущее поле обязательным, если другое поле активно.
         *
         * @param message - Сообщение об ошибке, если проверка не пройдена.
         * @param dependentFieldName - Имя поля, определяющее, является ли данное поле обязательным.
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined.
         */
        this.requiredIfDependentFieldExists = (message, dependentFieldName) => (value, allValues) => {
            const dependentFieldValue = allValues === null || allValues === void 0 ? void 0 : allValues[dependentFieldName];
            if (dependentFieldValue && !value) {
                return message;
            }
            return undefined;
        };
        /**
         * Создать функцию валидации количества элементов в массиве
         *
         * @param message - Сообщение об ошибке
         * @param options - Параметры валидации
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.arrayLength = (message, options) => (value) => {
            if (value === undefined || value === null) {
                return undefined;
            }
            if (Array.isArray(value)) {
                const length = value.length;
                if (options.min !== undefined && length < options.min) {
                    return message;
                }
                if (options.max !== undefined && length > options.max) {
                    return message;
                }
            }
            return undefined;
        };
        /**
         * Определить является ли текущее валидируемое поле больше чем значение из передаваемого в options поля
         * Текущее валидируемое поле зависит от другого поля (определено в options.field).
         *
         * @typeParam FieldType - Тип валидируемого поля
         * @param message - Сообщение об ошибке
         * @param options - Параметры зависимости
         * @returns Функция валидатора final-form
         */
        this.maxValueWithConnectedFieldValidator = (message = 'Обязательное поле', options) => (value, allValues) => {
            const connectedValue = at(allValues, options.field)[0];
            return Number(value) > Number(connectedValue) ? message : undefined;
        };
        /**
         * Создать функцию, которая будет возвращать предупреждающее сообщение,
         * если в тексте отсутствует поддерживаемый протокол
         *
         * @param message - Сообщение об ошибке
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.containsSupportedProtocol = (message = 'Ссылка должна содержать поддерживаемый протокол') => (value) => {
            if (!value)
                return undefined;
            const SUPPORTED_PROTOCOLS = [
                'http:',
                'https:',
                'mailto:',
                'sms:',
                'tel:',
            ];
            try {
                const parsedUrl = new URL(value);
                const hasSupportedProtocol = SUPPORTED_PROTOCOLS.includes(parsedUrl.protocol);
                if (!hasSupportedProtocol) {
                    return message;
                }
                if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
                    const hasDoubleSlash = value.includes('://');
                    return hasDoubleSlash
                        ? undefined
                        : 'Ссылка должна содержать // после протокола';
                }
                return undefined;
            }
            catch (_a) {
                return message;
            }
        };
        /* eslint-disable no-magic-numbers */
        /**
         * Проверяет значение по алгоритму Луна (валидация номера банковской карты)
         *
         * @param message - Сообщение об ошибке
         * @returns Функция валидации, возвращающая сообщение об ошибке или undefined
         */
        this.luhnAlgorithm = (message) => (value) => {
            if (!value)
                return undefined;
            if (!/^\d+$/.test(value)) {
                return message;
            }
            const cleaned = value.replace(/[\s-]/g, '');
            const reversed = cleaned.split('').reverse();
            let total = 0;
            for (let i = 0; i < reversed.length; i++) {
                let digit = parseInt(reversed[i]);
                if (i % 2 === 1) {
                    digit *= 2;
                    if (digit > 9)
                        digit -= 9;
                }
                total += digit;
            }
            return total % 10 === 0 ? undefined : message;
        };
    }
    /**
     * Проверить объект на соответствие валидационной схеме и в случае несоответствия выбросить исключение
     *
     * @param dto - Валидируемый объект
     * @param constructor - Класс, описывающий валидационную схему
     * @param groups - Массив групп, которые встречаются в валидационной схеме
     */
    validateOrReject(dto, constructor, groups) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const value = constructor ? plainToClass(constructor, dto) : dto;
                yield validateOrReject(value, { groups });
            }
            catch (err) {
                throw new AppError('client', {
                    code: 400,
                    message: 'Bad Request',
                    error: 'Bad Request',
                });
            }
        });
    }
    /**
     * Проверить объект на соответствие валидационной схеме и вернуть объект с ошибками
     *
     * @typeParam T - Тип объекта
     * @param dto - Валидируемый объект
     * @param constructor - Класс, описывающий валидационную схему
     * @param groups - Массив групп, которые встречаются в валидационной схеме
     * @returns Объект с ошибками валидации
     */
    validate(dto, constructor, groups) {
        return __awaiter(this, void 0, void 0, function* () {
            const value = constructor ? plainToClass(constructor, dto) : dto;
            const errors = yield validate(value, {
                validationError: { target: false, value: false },
                groups,
            });
            const result = errorsToAllStrings(value, errors);
            return result;
        });
    }
};
ValidationService = __decorate([
    injectable(),
    __metadata("design:paramtypes", [FileType])
], ValidationService);
export { ValidationService };
